import { useCallback, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { DIALOG_CLOSE_BUTTON_SX } from "../../../constants/dialogCloseButtonStyles";
import { brandContainedButtonSx } from "../../../constants/brandContainedButtonStyles";
import { useBrandThemeColors } from "../../../branding";
import { withoutBackdropClose } from "../../../utils/withoutBackdropClose";

const ACCEPTED_EXTENSIONS = [".csv"];
const ACCEPTED_MIME_TYPES = ["text/csv", "application/csv"];
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;
const NO_FILE_SELECTED_ERROR = "Please select a file before confirming.";
const SUPPORTED_FORMATS_HINT = "Supported File Formats: .csv";

function getFileExtension(name: string): string {
  const dotIndex = name.lastIndexOf(".");
  return dotIndex >= 0 ? name.slice(dotIndex).toLowerCase() : "";
}

function validateUploadFile(file: File): string | null {
  const extension = getFileExtension(file.name);
  const hasValidExtension = ACCEPTED_EXTENSIONS.includes(extension);
  const hasValidMime =
    file.type === "" || ACCEPTED_MIME_TYPES.includes(file.type);

  if (!hasValidExtension || !hasValidMime) {
    return SUPPORTED_FORMATS_HINT;
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return "Upto 1 file allowed; max 50 mb each";
  }
  return null;
}

type ExtractFromFileDialogProps = Readonly<{
  open: boolean;
  confirming?: boolean;
  onClose: () => void;
  onConfirm: (file: File) => void | Promise<void>;
}>;

export default function ExtractFromFileDialog({
  open,
  confirming = false,
  onClose,
  onConfirm,
}: ExtractFromFileDialogProps) {
  const { primaryColor, primaryFontColor } = useBrandThemeColors();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const resetState = useCallback(() => {
    setSelectedFile(null);
    setDragActive(false);
    setValidationError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  const handleClose = useCallback(() => {
    if (confirming) return;
    resetState();
    onClose();
  }, [confirming, onClose, resetState]);

  const handleSelectFile = useCallback((file: File | undefined) => {
    if (!file) return;
    const error = validateUploadFile(file);
    if (error) {
      setSelectedFile(null);
      setValidationError(error);
      return;
    }
    setSelectedFile(file);
    setValidationError(null);
  }, []);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleSelectFile(event.target.files?.[0]);
    },
    [handleSelectFile],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setDragActive(false);
      handleSelectFile(event.dataTransfer.files?.[0]);
    },
    [handleSelectFile],
  );

  const handleConfirm = useCallback(async () => {
    if (confirming) return;
    if (!selectedFile) {
      setValidationError(NO_FILE_SELECTED_ERROR);
      return;
    }
    await onConfirm(selectedFile);
  }, [confirming, onConfirm, selectedFile]);

  const outlinedPrimarySx = {
    textTransform: "none",
    fontWeight: 500,
    borderColor: primaryColor,
    color: primaryColor,
    "&:hover": {
      borderColor: primaryColor,
    },
  } as const;

  return (
    <Dialog
      open={open}
      onClose={withoutBackdropClose(handleClose)}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px" } }}
    >
      <DialogTitle sx={{ pb: 1, pr: 6 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              component="span"
              sx={{ fontWeight: 600, fontSize: "1.125rem", color: "#262626" }}
            >
              Extract From File
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#6b7280", mt: 0.5, lineHeight: 1.5 }}
            >
              Extract relevant data from an uploaded file to pre-fill the form
            </Typography>
          </Box>
          <IconButton
            aria-label="Close"
            onClick={handleClose}
            disabled={confirming}
            sx={{
              ...DIALOG_CLOSE_BUTTON_SX,
              position: "absolute",
              right: 16,
              top: 16,
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1, pb: 2 }}>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept={ACCEPTED_EXTENSIONS.join(",")}
          onChange={handleInputChange}
        />

        <Box
          role="button"
          tabIndex={0}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              fileInputRef.current?.click();
            }
          }}
          onDragEnter={(event) => {
            event.preventDefault();
            setDragActive(true);
          }}
          onDragOver={(event) => {
            event.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={(event) => {
            event.preventDefault();
            setDragActive(false);
          }}
          onDrop={handleDrop}
          sx={{
            border: "1px dashed",
            borderColor: dragActive ? primaryColor : "#d1d5db",
            borderRadius: "8px",
            bgcolor: dragActive ? "rgba(37, 99, 235, 0.04)" : "#fff",
            py: 4,
            px: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            cursor: "pointer",
          }}
        >
          <FileUploadOutlinedIcon sx={{ fontSize: 32, color: "#9ca3af", mb: 1.5 }} />
          <Typography sx={{ fontSize: "0.875rem", color: "#262626", mb: 0.5 }}>
            Drag & drop files here or click on select
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: "#9ca3af", lineHeight: 1.5 }}>
            {SUPPORTED_FORMATS_HINT}
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: "#9ca3af", lineHeight: 1.5 }}>
            Upto 1 file allowed; max 50 mb each
          </Typography>
        </Box>

        {validationError ? (
          <Typography variant="body2" sx={{ color: "error.main", mt: 1.5 }}>
            {validationError}
          </Typography>
        ) : null}

        {selectedFile ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              mt: 2,
            }}
          >
            <AttachFileIcon sx={{ fontSize: 18, color: primaryColor }} />
            <Typography
              variant="body2"
              sx={{ color: primaryColor, fontWeight: 500 }}
            >
              {selectedFile.name}
            </Typography>
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1 }}>
        <Button variant="outlined" onClick={handleClose} disabled={confirming} sx={outlinedPrimarySx}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleConfirm()}
          disabled={confirming}
          sx={{
            ...brandContainedButtonSx,
            bgcolor: `${primaryColor} !important`,
            color: `${primaryFontColor} !important`,
            "&:hover": {
              bgcolor: `${primaryColor} !important`,
            },
          }}
        >
          {confirming ? "Confirming…" : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export { validateUploadFile };
